<template>
  <div class="data-table-column-header">
    <button
      :class="[
        'data-table-column-header__label',
        {
          'data-table-column-header__label--sortable':
            sortable || (filterable && filterType === 'alphabetic'),
        },
      ]"
      @click="filterable && filterType === 'alphabetic' ? handleAlphabeticFilter() : handleSort()"
      :disabled="!sortable && !(filterable && filterType === 'alphabetic')"
    >
      <span class="data-table-column-header__text">{{ label }}</span>
      <span
        v-if="sortable || (filterable && filterType === 'alphabetic')"
        class="data-table-column-header__sort-icon"
      >
        <Icon
          v-if="sortDirection === 'asc'"
          icon="mdi:arrow-up"
          class="data-table-column-header__icon"
        />
        <Icon
          v-else-if="sortDirection === 'desc'"
          icon="mdi:arrow-down"
          class="data-table-column-header__icon"
        />
        <Icon
          v-else
          icon="mdi:unfold-more-horizontal"
          class="data-table-column-header__icon data-table-column-header__icon--default"
        />
      </span>
    </button>

    <!-- Multi-select filter -->
    <div
      v-if="
        filterable && filterType === 'multi-select' && filterOptions && filterOptions.length > 0
      "
      class="data-table-column-header__filter-wrapper"
      ref="filterWrapperRef"
    >
      <button
        :class="[
          'data-table-column-header__filter-button',
          {
            'data-table-column-header__filter-button--active': hasActiveFilters,
          },
        ]"
        @click="toggleFilter"
      >
        <Icon icon="mdi:filter-outline" />
      </button>

      <Transition name="filter-popover">
        <div v-if="filterOpen" class="data-table-column-header__filter-popover">
          <div class="data-table-column-header__filter-header">
            <span class="data-table-column-header__filter-title">Filter by {{ label }}</span>
            <button
              v-if="hasActiveFilters"
              class="data-table-column-header__filter-clear"
              @click="clearFilters"
            >
              <Icon icon="mdi:close" />
              Clear
            </button>
          </div>
          <div class="data-table-column-header__filter-options">
            <div
              v-for="option in filterOptions"
              :key="option.value"
              class="data-table-column-header__filter-option"
              @click.stop="handleFilterToggle(option.value)"
            >
              <Checkbox
                :model-value="activeFilters.includes(option.value)"
                @update:model-value="() => handleFilterToggle(option.value)"
                @click.stop
                size="sm"
              />
              <!--
                The Checkbox's @click.stop above keeps a click on the checkbox itself
                from also bubbling up to the row's @click.stop="handleFilterToggle"
                handler — without it, clicking the checkbox would call
                handleFilterToggle twice (once via update:model-value, once via the
                row), toggling the filter on and back off in the same click.
              -->
              <Icon
                v-if="option.icon"
                :icon="option.icon"
                class="data-table-column-header__filter-option-icon"
              />
              <span class="data-table-column-header__filter-option-label">{{ option.label }}</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { Icon } from "@iconify/vue";
import Checkbox from "@/components/ui/Checkbox.vue";
import type { SortDirection, FilterOption } from "./types";

interface Props {
  label: string;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: (direction: SortDirection) => void;
  filterable?: boolean;
  filterType?: "multi-select" | "alphabetic";
  filterOptions?: FilterOption[];
  activeFilters?: string[];
  onFilter?: (values: string[]) => void;
}

const props = withDefaults(defineProps<Props>(), {
  sortable: false,
  filterable: false,
  filterOptions: () => [],
  activeFilters: () => [],
});

const filterOpen = ref(false);
const filterWrapperRef = ref<HTMLElement | null>(null);
const hasActiveFilters = computed(() => props.activeFilters.length > 0);

// Alphabetic filter state
const alphabeticFilterDirection = computed(() => {
  if (props.filterType === "alphabetic" && props.sortable) {
    return props.sortDirection;
  }
  return null;
});

const handleSort = () => {
  if (!props.sortable || !props.onSort) return;

  if (props.sortDirection === null) {
    props.onSort("asc");
  } else if (props.sortDirection === "asc") {
    props.onSort("desc");
  } else {
    props.onSort(null);
  }
};

const toggleFilter = () => {
  filterOpen.value = !filterOpen.value;
};

const handleFilterToggle = (value: string) => {
  if (!props.onFilter) return;

  if (props.activeFilters.includes(value)) {
    props.onFilter(props.activeFilters.filter((f) => f !== value));
  } else {
    props.onFilter([...props.activeFilters, value]);
  }
};

const clearFilters = () => {
  props.onFilter?.([]);
};

const handleAlphabeticFilter = () => {
  if (!props.sortable || !props.onSort) return;

  // Cycle through: null → asc → desc → null
  if (props.sortDirection === null) {
    props.onSort("asc");
  } else if (props.sortDirection === "asc") {
    props.onSort("desc");
  } else {
    props.onSort(null);
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (filterWrapperRef.value && !filterWrapperRef.value.contains(event.target as Node)) {
    filterOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.data-table-column-header {
  display: flex;
  align-items: center;
  gap: 4px;
}

.data-table-column-header__label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: none;
  border: none;
  padding: 0;
  cursor: default;
}

.data-table-column-header__label--sortable {
  cursor: pointer;
  transition: color 0.2s ease;
}

.data-table-column-header__label--sortable:hover {
  color: #1f2937;
}

.data-table-column-header__text {
  user-select: none;
}

.data-table-column-header__sort-icon {
  display: flex;
  align-items: center;
  color: #3e3efe;
}

.data-table-column-header__icon {
  font-size: 14px;
  width: 14px;
  height: 14px;
}

.data-table-column-header__icon--default {
  color: #9ca3af;
}

.data-table-column-header__filter {
  display: inline-flex;
}

.data-table-column-header__filter-button {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s ease;
  border-radius: 4px;
}

.data-table-column-header__filter-button:hover {
  background-color: #f3f4f6;
}

.data-table-column-header__filter-button--active {
  color: #3e3efe;
}

.data-table-column-header__filter-button :deep(.iconify) {
  font-size: 14px;
  width: 14px;
  height: 14px;
}

.data-table-column-header__alphabetic-filter {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s ease;
  border-radius: 4px;
}

.data-table-column-header__alphabetic-filter:hover {
  background-color: #f3f4f6;
}

.data-table-column-header__alphabetic-filter--active {
  color: #3e3efe;
}

.data-table-column-header__alphabetic-filter :deep(.iconify) {
  font-size: 14px;
  width: 14px;
  height: 14px;
}

.data-table-column-header__filter-wrapper {
  position: relative;
  display: inline-flex;
}

.data-table-column-header__filter-popover {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 50;
  min-width: 160px;
  padding: 4px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
}

.data-table-column-header__filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 4px;
}

.data-table-column-header__filter-title {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.data-table-column-header__filter-clear {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 20px;
  padding: 0 4px;
  font-size: 12px;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
}

.data-table-column-header__filter-clear:hover {
  color: #1f2937;
}

.data-table-column-header__filter-clear :deep(.iconify) {
  font-size: 12px;
  width: 12px;
  height: 12px;
}

.data-table-column-header__filter-options {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.data-table-column-header__filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.data-table-column-header__filter-option:hover {
  background-color: #f3f4f6;
}

.data-table-column-header__filter-option-label {
  font-size: 14px;
  color: #1f2937;
}

.data-table-column-header__filter-option-icon {
  font-size: 15px;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  color: #6b7280;
}

.filter-popover-enter-active,
.filter-popover-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.filter-popover-enter-from,
.filter-popover-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
